import React from "react";
import { Box, Option } from "@mui/joy";
import type {
  EquipmentModel,
  EquipmentAttributeDefinition,
} from "../../../api/equipment/Equipment.typed";
import CommonModal from "../../common/CommonModal";
import DarkTypography from "../../common/DarkTypography";
import DarkCheckbox from "../../common/DarkCheckbox";
import DarkSelect from "../../common/DarkSelect";
import { colorTokens } from "../../../styles/globalStyles";
import { getQRExportPixelSize, QR_EXPORT_QR_PX } from "../../../utils/equipmentQRExportUtils";

interface EquipmentExportModalProps {
  open: boolean;
  selectedModel: EquipmentModel | undefined;
  exportAttrIds: string[];
  onClose: () => void;
  onAttributeToggle: (attrId: string) => void;
  onExport: (options?: { filenameAttrId?: string }) => void;
}

export default function EquipmentExportModal({
  open,
  selectedModel,
  exportAttrIds,
  onClose,
  onAttributeToggle,
  onExport,
}: EquipmentExportModalProps) {
  const [filenameAttrId, setFilenameAttrId] = React.useState<string | null>(null);

  if (!open) return null;

  const singlePx = getQRExportPixelSize(exportAttrIds.length);

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title="Export QR"
      size="xl"
      fitContentHeight
      onConfirm={() => onExport({ filenameAttrId: filenameAttrId || undefined })}
      confirmLabel="Exporter"
    >
      <DarkTypography level="h4" sx={{ mb: 1.5 }}>
        Champs à imprimer
      </DarkTypography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {selectedModel?.Attributes.map((a: EquipmentAttributeDefinition) => (
          <DarkCheckbox
            key={a.ID}
            label={a.Label}
            checked={exportAttrIds.includes(a.ID)}
            onChange={() => onAttributeToggle(a.ID)}
          />
        ))}
      </Box>
      <Box sx={{ mt: 2 }}>
        <DarkTypography level="body-md" sx={{ color: colorTokens.text.primary, mb: 0.5 }}>
          Nom de fichier (attribut)
        </DarkTypography>
        <DarkSelect
          placeholder="Sélectionner un attribut"
          value={filenameAttrId}
          onChange={(_, val) => setFilenameAttrId(val)}
          sx={{ minWidth: { xs: "100%", sm: 420 } }}
        >
          {(selectedModel?.Attributes || [])
            .filter((a: EquipmentAttributeDefinition) => a.Type === "string")
            .map((a: EquipmentAttributeDefinition) => (
              <Option key={a.ID} value={a.ID}>
                {a.Label}
              </Option>
            ))}
        </DarkSelect>
      </Box>
      <DarkTypography level="body-sm" sx={{ mt: 2, color: colorTokens.text.secondary }}>
        {`PNG ${singlePx.width} × ${singlePx.height} px (QR ${QR_EXPORT_QR_PX} px).`}
      </DarkTypography>
    </CommonModal>
  );
}
